import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';

import { SimpleBlockContent } from '@fhs/page-builder';
import { EnhancedInlineAlert, Text, tokens } from '@fhs/ui';
import {
  ILoyaltyQuestCartMessage,
  LoyaltyQuestCartMessageStatus,
} from '@fhs-legacy/frontend/src/generated/graphql-gateway';

export interface ChallengeCartMessageProps {
  cartMessage: Pick<ILoyaltyQuestCartMessage, 'content' | 'status' | 'title'>;
  /** Action displayed on warning messages */
  warningAction?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

export function ChallengeCartMessage({
  cartMessage,
  warningAction,
  style,
  testID,
}: ChallengeCartMessageProps) {
  const { content, status, title } = cartMessage;
  const message = <SimpleBlockContent value={content} normalText={{ size: 'sm' }} />;

  switch (status) {
    // Unmet requirements are displayed with the warning style, as they can be fixed by changing the cart.
    case LoyaltyQuestCartMessageStatus.ERROR:
      return (
        <EnhancedInlineAlert
          title={title ?? undefined}
          titleWeight="bold"
          status="warning"
          message={message}
          style={style}
          testID={testID}
        />
      );

    case LoyaltyQuestCartMessageStatus.WARNING:
      return (
        <EnhancedInlineAlert
          title={title ?? undefined}
          titleWeight="bold"
          isDismissable
          status="warning"
          message={message}
          action={warningAction}
          style={style}
          testID={testID}
        />
      );

    case LoyaltyQuestCartMessageStatus.SUCCESS:
      return (
        <View testID={testID} style={[styles.successContainer, style]}>
          {title && <Text weight="bold" style={styles.successTitle}>{title}</Text>}
          {message}
        </View>
      );

    default:
      return null;
  }
}

const styles = StyleSheet.create({
  // Same spacing and title as EnhancedInlineAlert, without the icon.
  successContainer: {
    justifyContent: 'center',
    minHeight: 69,
    gap: 4,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: tokens.colors.$successOpacity10,
  },
  successTitle: {
    fontSize: 14,
    lineHeight: 20,
  },
});
